import { format } from 'date-fns'
import { CalendarIcon, CircleSlash2Icon, LockKeyhole, X } from 'lucide-react'
import { DateRange, DayButton } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar, CalendarDayButton } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'

const toDisplayLabel = (value: string) =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const getUserParticipant = (
  event: wowaudit.EventReference,
  currentUserId?: number,
) => {
  if (!currentUserId) return undefined
  return event.participantReferences.find(
    (participant) => participant.characterReference.userId === currentUserId,
  )
}

type Props = {
  className?: string
  date?: DateRange
  setDate?: (date: DateRange | undefined) => void
  placeholder?: string
  isClearable?: boolean
  minDate?: Date
  maxDate?: Date
  dayEvents?: wowaudit.EventReference[]
  currentUserId?: number
}

export function DateRangePicker({
  className,
  date,
  setDate,
  placeholder = 'Custom range',
  isClearable,
  minDate,
  maxDate,
  dayEvents,
  currentUserId,
}: Props) {
  const { t } = useTranslation()
  const [pendingDate, setPendingDate] = useState<DateRange | undefined>(date)
  const hasDayEvents = !!dayEvents?.length
  const eventsByDate = useMemo(
    () =>
      (dayEvents ?? []).reduce<Record<string, wowaudit.EventReference[]>>(
        (acc, event) => {
          const dateKey = format(new Date(event.date), 'yyyy-MM-dd')
          if (!acc[dateKey]) {
            acc[dateKey] = []
          }
          acc[dateKey].push(event)
          return acc
        },
        {},
      ),
    [dayEvents],
  )

  const handleSelect = (selectedDate: DateRange | undefined) => {
    if (
      selectedDate?.from &&
      selectedDate?.to &&
      selectedDate.from < selectedDate.to
    ) {
      setDate?.(selectedDate)
      setPendingDate(selectedDate)
    } else {
      setPendingDate(selectedDate)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDate?.(undefined)
    setPendingDate(undefined)
  }

  const DayButtonWithEvents = (
    props: React.ComponentProps<typeof DayButton>,
  ) => {
    const dateKey = format(props.day.date, 'yyyy-MM-dd')
    const events = eventsByDate[dateKey] ?? []
    const indicatorParticipant = events
      .map((event) => getUserParticipant(event, currentUserId))
      .find((participant) => !!participant)
    const dayButton = (
      <CalendarDayButton
        {...props}
        className={cn(props.className, hasDayEvents ? 'pb-3' : '')}
      >
        {props.children}
        {indicatorParticipant ? (
          <span className='pointer-events-none absolute bottom-0 left-1/2 flex h-3.5 w-3.5 -translate-x-1/2 items-center justify-center'>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='pointer-events-auto flex h-3.5 w-3.5 items-center justify-center'>
                  <EventStatusIcon
                    status={indicatorParticipant.status}
                    size='tiny'
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side='top'>
                {events.map((event, index) => {
                  const participant = getUserParticipant(event, currentUserId)

                  return (
                    <div key={`${dateKey}-${event.id}-${index}`}>
                      <div className='space-y-0.5'>
                        <div className='font-medium flex items-center gap-1.5'>
                          {event.status === 'locked' ? (
                            <LockKeyhole className='h-3 w-3 text-warning-foreground' />
                          ) : null}
                          {event.status === 'cancelled' ? (
                            <CircleSlash2Icon className='h-3 w-3 text-destructive-foreground' />
                          ) : null}
                          {event.title || toDisplayLabel(event.activityType)}
                          {event.optional ? (
                            <span className='text-xs text-info-foreground'>
                              (optional)
                            </span>
                          ) : null}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          {event.startTime} - {event.finishTime},{' '}
                          {toDisplayLabel(
                            event.primaryDifficulty ?? event.activityType,
                          )}
                        </div>
                        {participant ? (
                          <div className='text-xs text-muted-foreground flex items-center gap-1.5'>
                            <EventStatusIcon
                              status={participant.status}
                              size='small'
                            />
                            <span>
                              {t(
                                `settings.raidsAvailableStatuses.options.${participant.status}`,
                              )}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </TooltipContent>
            </Tooltip>
          </span>
        ) : null}
      </CalendarDayButton>
    )

    return dayButton
  }

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id='date'
            variant={'outline'}
            className={cn(
              'w-48 justify-start text-left flex items-center gap-2 font-normal hover:bg-card hover:text-foreground',
              !date && 'text-muted-foreground hover:text-muted-foreground',
              isClearable && date && 'pr-2',
            )}
          >
            <CalendarIcon size={16} className='min-w-4' />
            <span className='flex-1'>
              {date?.from ? (
                date.to ? (
                  <>
                    {format(date.from, 'LLL dd')} - {format(date.to, 'LLL dd')}
                  </>
                ) : (
                  format(date.from, 'LLL dd')
                )
              ) : (
                <span>{placeholder}</span>
              )}
            </span>
            {isClearable && date && (
              <button
                type='button'
                className='ml-1 rounded-full outline-hidden focus:ring-1 focus:ring-ring'
                onMouseDown={handleClear}
                onClick={handleClear}
              >
                <X className='opacity-50 hover:opacity-100 transition-opacity p-1' />
              </button>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <TooltipProvider delayDuration={300} disableHoverableContent={true}>
            <Calendar
              mode='range'
              required={false}
              weekStartsOn={1}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
              ]}
              defaultMonth={pendingDate?.from}
              selected={pendingDate}
              onSelect={handleSelect}
              components={{ DayButton: DayButtonWithEvents }}
              numberOfMonths={2}
              pagedNavigation={true}
              captionLayout='dropdown-years'
            />
          </TooltipProvider>
        </PopoverContent>
      </Popover>
    </div>
  )
}
